import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Turnstile, type AppearanceMode } from '@marsidev/react-turnstile';

import { CLOUDFLARE_TURNSTILE_ERROR_CODES } from 'lib/cloudflare_turnstile_error_codes';
import { Ownership } from 'lib/generated_constants/ownership';
import { shouldIgnoreTurnstileError } from 'lib/telemetry/ignore_errors';

import { Alert, AlertType } from 'design_system_components/alert';
import { useAppsignal } from 'hooks/use_appsignal';
import { useEnvVars } from 'hooks/use_env_vars';

import * as styles from './cloudflare_turnstile.module.css';

const TURNSTILE_ERROR_MESSAGE = 'Cloudflare Turnstile error';
const TURNSTILE_INITIALIZATION_TIMEOUT = 10_000;
const TURNSTILE_INITIALIZATION_TIMEOUT_ERROR = 'initialization-timeout';
const TURNSTILE_SCRIPT_LOAD_ERROR = 'script-load-error';
const TURNSTILE_UNSUPPORTED_ERROR = 'unsupported-browser';

function turnstileErrorGuidance(code: string) {
  if (code === TURNSTILE_UNSUPPORTED_ERROR) {
    return 'Update your browser or try another supported browser, then reload this page.';
  }

  // Only local failures and Cloudflare's iframe error identify this visitor action.
  if (
    code === CLOUDFLARE_TURNSTILE_ERROR_CODES.IFRAME_LOAD_ERROR ||
    code === TURNSTILE_SCRIPT_LOAD_ERROR ||
    code === TURNSTILE_INITIALIZATION_TIMEOUT_ERROR
  ) {
    return (
      <>
        Allow{' '}
        <span className={styles.challengeDomain}>
          challenges.cloudflare.com
        </span>{' '}
        in your content blocker, then reload this page. If the problem
        continues, either disable content blockers or try another
        browser/network.
      </>
    );
  }

  // Widget errors may be configuration failures, so avoid prescribing visitor action by default.
  return (
    <>
      Reload this page and try again. If security verification remains
      unavailable, contact{' '}
      <a href="mailto:support@userinterviews.com">support@userinterviews.com</a>
      .
    </>
  );
}

type CloudflareTurnstileProps = {
  // The server checks this flow identifier to prevent cross-form token reuse.
  action: string;
  onError?: () => void;
  onExpire?: () => void;
  onSuccess?: (token: string) => void;
  options?: {
    appearance?: AppearanceMode;
    responseField?: boolean;
  };
  submitButtonId?: string;
};

export type CloudflareTurnstileConfig = {
  action: string;
  enabled: boolean;
};

export function CloudflareTurnstile({
  action,
  onError = () => {},
  onExpire = () => {},
  onSuccess = () => {},
  options,
  submitButtonId,
}: CloudflareTurnstileProps) {
  const { cloudflareTurnstileCaptchaSiteKey } = useEnvVars();
  // Turnstile is shared infrastructure, so its failures stay with its owner rather than the host page's owner.
  const appsignal = useAppsignal({ owner: Ownership.ENTERPRISE_PLATFORM });
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const initializationFinished = useRef(false);

  const setSubmitDisabled = useCallback(
    (disabled: boolean) => {
      if (submitButtonId) {
        document
          .getElementById(submitButtonId)
          ?.toggleAttribute('disabled', disabled);
      }
    },
    [submitButtonId],
  );

  const handleError = useCallback(
    (code: string) => {
      const ignored =
        code === TURNSTILE_UNSUPPORTED_ERROR ||
        shouldIgnoreTurnstileError(code);
      initializationFinished.current = true;
      setErrorCode(code);
      setSubmitDisabled(true);
      onError();

      if (appsignal && !ignored) {
        appsignal.sendError(new Error(`${TURNSTILE_ERROR_MESSAGE}: ${code}`));
      }

      return ignored;
    },
    [appsignal, onError, setSubmitDisabled],
  );

  const handleErrorRef = useRef(handleError);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!initializationFinished.current) {
        handleErrorRef.current(TURNSTILE_INITIALIZATION_TIMEOUT_ERROR);
      }
    }, TURNSTILE_INITIALIZATION_TIMEOUT);

    return () => clearTimeout(timeout);
  }, []);

  const handleExpire = () => {
    setSubmitDisabled(true);
    onExpire();
  };

  const handleSuccess = (token: string) => {
    initializationFinished.current = true;
    setErrorCode(null);
    setSubmitDisabled(false);
    onSuccess(token);
  };

  return (
    <div className={styles.container}>
      {errorCode !== null && (
        <div className={styles.error} role="alert">
          <Alert
            message={turnstileErrorGuidance(errorCode)}
            title="Security verification unavailable"
            type={AlertType.ERROR}
          />
        </div>
      )}
      <Turnstile
        options={{ ...options, action }}
        scriptOptions={{
          onError: () => handleError(TURNSTILE_SCRIPT_LOAD_ERROR),
        }}
        siteKey={cloudflareTurnstileCaptchaSiteKey || ''}
        onError={handleError}
        onExpire={handleExpire}
        onSuccess={handleSuccess}
        onUnsupported={() => handleError(TURNSTILE_UNSUPPORTED_ERROR)}
        onWidgetLoad={() => {
          initializationFinished.current = true;
          setErrorCode(null);
        }}
      />
    </div>
  );
}
